/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from "axios";
import appConfig from "./apiConfig";
import { storageService } from "./storageService";

class ApiService {
  private axiosClient: AxiosInstance;

  // Variables to handle multiple simultaneous 401 errors
  private isRefreshing = false;
  private failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

  constructor() {
    this.axiosClient = axios.create({
      baseURL: appConfig.getBaseUrl(),
    });

    // Request Interceptor
    this.axiosClient.interceptors.request.use(
      (config) => {
        config.baseURL = appConfig.getBaseUrl();
        const token = storageService.getItem("accessToken");
        const headers = (config.headers ?? {}) as any;
        if (!headers.Authorization && token) {
          headers.Authorization = `Bearer ${token}`;
        }
        config.headers = headers;
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response Interceptor
    this.axiosClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Failsafe: if there is no config attached to the error, reject immediately
        if (!originalRequest) return Promise.reject(error);

        const currentPath = window.location.pathname;
        const isAuthFlow = currentPath.includes("/login") || currentPath.includes("/select-organization");
        const isAuthEndpoint = originalRequest.url?.includes("/login") || originalRequest.url?.includes("/refresh-token");

        // Check if the error is 401 and we haven't already retried this exact request
        if (error.response && error.response.status === 401 && !originalRequest._retry && !isAuthEndpoint) {

          if (this.isRefreshing) {
            // If already refreshing, pause this request and add it to the queue
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest._retry = true; // Mark queued requests so they don't loop

                // Safely apply headers for Axios 1.x
                if (originalRequest.headers && typeof originalRequest.headers.set === 'function') {
                  originalRequest.headers.set('Authorization', `Bearer ${token}`);
                } else {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                }
                return this.axiosClient(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          // 1. Get the refresh token from localStorage — keeps it alive across
          //    browser close/reopen and across tabs (sessionStorage would be wiped)
          const refreshToken = storageService.getItem("refreshToken");

          // 2. Get the email (from stored user profile, or fallback to login session)
          const storedUser = storageService.getItem("user");
          let email = sessionStorage.getItem("loginEmail");
          if (storedUser) {
            try {
              email = JSON.parse(storedUser).email || email;
            } catch (e) {
              // Ignore parse error and fallback to loginEmail
            }
          }

          // 3. Get the subdomain from localStorage (moved from sessionStorage so it
          //    persists across browser close/reopen and is shared across tabs)
          const activeSubdomain = storageService.getItem("activeSubdomain");
          // const subDomain = activeSubdomain;
          console.log("Active domin: ", activeSubdomain);

          // If we are missing critical auth data, perform standard logout
          if (!refreshToken || !email) {
            this.isRefreshing = false;
            this.handleLogout(isAuthFlow);
            return Promise.reject(error);
          }

          let newAccessToken: string | null = null;

          try {
            // Execute the refresh token API call bypassing interceptors
            const { data } = await axios.post(`${appConfig.getBaseUrl()}api/refresh-token`, {
              refreshToken: refreshToken,
              email: email,
              subDomain: activeSubdomain
            });

            newAccessToken = data.accessToken ?? null;
            // Guard: only write when token is a real value.
            // storageService.setItem(key, null) used to store the string "null"
            // which is truthy and caused every subsequent request to send "Bearer null".
            if (newAccessToken) {
              storageService.setItem("accessToken", newAccessToken);
            }

            // Update refresh token in localStorage so it survives browser close/reopen
            if (data.refreshToken) {
              storageService.setItem("refreshToken", data.refreshToken);
            }

            // Dispatch event to update UI components
            window.dispatchEvent(new Event("auth-token-updated"));

          } catch (refreshError) {
            // If the refresh token call fails, clear queue and log out
            this.processQueue(refreshError, null);
            this.isRefreshing = false;
            this.handleLogout(isAuthFlow);
            return Promise.reject(refreshError);
          }

          // CRITICAL FIX: Explicitly release the lock BEFORE returning the retry promise
          this.isRefreshing = false;

          if (newAccessToken) {
            // Resolve all queued requests with the new token
            this.processQueue(null, newAccessToken);

            // Safely apply headers for Axios 1.x
            if (originalRequest.headers && typeof originalRequest.headers.set === 'function') {
              originalRequest.headers.set('Authorization', `Bearer ${newAccessToken}`);
            } else {
              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
            }

            // Retry the original failed request
            return this.axiosClient(originalRequest);
          } else {
            // Refresh API returned HTTP 200 but no accessToken in the body.
            // Without this branch the failedQueue would leak — requests queued
            // during the refresh window would hang forever because processQueue
            // is only called inside the `if (newAccessToken)` block above.
            const noTokenError = new Error("Token refresh succeeded but returned no access token");
            this.processQueue(noTokenError, null);
            this.handleLogout(isAuthFlow);
            return Promise.reject(noTokenError);
          }
        }

        // Normal 401 fallback if retry has already happened or isn't possible
        if (error.response && error.response.status === 401) {
          this.handleLogout(isAuthFlow);
        }

        return Promise.reject(error);
      }
    );
  }

  // Helper method to process paused API requests
  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token as string);
      }
    });
    this.failedQueue = [];
  }

  // Helper method to handle a hard logout
  private handleLogout(isAuthFlow: boolean) {
    // Clear all auth tokens from localStorage
    storageService.removeItem("accessToken");
    storageService.removeItem("refreshToken");

    // Clear activeSubdomain from localStorage (moved from sessionStorage)
    storageService.removeItem("activeSubdomain");

    // Clear remaining session metadata so re-login does not pick up stale values
    sessionStorage.removeItem("refreshToken");      // legacy: remove old sessionStorage copy if present
    sessionStorage.removeItem("activeSubdomain");   // legacy: remove old sessionStorage copy if present
    sessionStorage.removeItem("loginEmail");
    sessionStorage.removeItem("loginPassword");
    sessionStorage.removeItem("isMultiOrg");
    sessionStorage.removeItem("authLoginMode");
    sessionStorage.removeItem("passwordOrganizations");

    // Only trigger a hard redirect if they are NOT in the login flow
    if (!isAuthFlow) {
      window.location.href = `${window.location.origin}/login`;
    }
  }

  public async get(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    const response = await this.axiosClient.get(url, config);
    return response;
  }

  public async post(
    url: string,
    data: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    const response: AxiosResponse = await this.axiosClient.post(
      url,
      data,
      config,
    );
    return response;
  }

  public async put(url: string, data: any): Promise<AxiosResponse> {
    const config: any = {};

    if (data instanceof FormData) {
      config.headers = {
        "Content-Type": undefined,
      };
    }

    const response = await this.axiosClient.put(url, data, config);
    return response;
  }

  public async patch(url: string, data: any): Promise<AxiosResponse> {
    const response = await this.axiosClient.patch(url, data);
    return response;
  }

  public async delete(url: string, data?: any): Promise<AxiosResponse> {
    const response = await this.axiosClient.delete(url, data);
    return response;
  }
}

const apiService = new ApiService();

export default apiService;